/**
 *
 * @module httpClient
 *
 */

import Check from 'check-types';
import Logging from './../../../../logging/entry';
import Configuration from './../../../configuration/entry';
import Exception from './../../../exception/entry';
import CoreHttpClientProvider from './../../shared/coreHttpClientProvider';

const HttpMethod = Configuration.HttpMethod;
const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    like
} = Check.assert;

/**
 *
 * @class HttpClient
 * @extends {CoreHttpClientProvider}
 * @desc HttpClient implementation for Trilithium platforms.
 *
 * @example
 * const httpClient = new HttpClient(logger);
 *
 */
export default class HttpClient extends CoreHttpClientProvider {

    /**
     *
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(logger = Logging) {

        const errorMessage = {
            ctor: 'HttpClient(logger)',
            get httpClient() {
                return `${this.ctor} needs engine.storage.local Instance`;
            }
        };

        const httpClientInstance = (function httpClientInstance() {
            return Check.assigned(global)
                && Check.assigned(global.engine)
                && Check.function(global.engine.createHttpClient);
        }());

        Check.assert(httpClientInstance, errorMessage.httpClient, InvalidArgumentException);

        super(logger);

        /**
         *
         * @access private
         * @type {Object}
         * @desc native trilithium HttpClient implementation
         *
         */
        this.client = global.engine.createHttpClient();

        this.logger.log(HttpClient.name, 'Created Trilithium Provider.');

    }

    /**
     *
     * @access public
     * @method post
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {NetworkException}
     * @throws {ProviderException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.post(options).then((response) => { console.log(response.data); });
     *
     */
    post(options) {

        const errorMessage = {
            method: 'HttpClient.post(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        const { optionsBaseline, client } = this;

        like(
            options,
            optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return new Promise((resolve) => {

            const { url, headers, body } = options;
            const { getResponseData, getHeaderWithKey, onError } = this;

            const request = client.createRequest(HttpMethod.POST, url, { headers });

            let response = null;

            /**
             *
             * @method onResponse
             * @param {Object} httpResponse
             *
             */
            request.onResponse = (httpResponse) => {
                response = httpResponse;
            };

            /**
             *
             * @method onComplete
             * @param {*} responseData
             * @param {Number} responseStatus
             * @returns {Promise<Object>}
             *
             */
            request.onComplete = (responseData, responseStatus) => {

                if (!responseStatus) {

                    return onError();

                }

                return resolve({
                    status: responseStatus,
                    data: getResponseData(responseData),
                    headers: {
                        get: (key) => {
                            return getHeaderWithKey(key, response);
                        }
                    }
                });

            };

            request.sendBody(body);
            request.start();

        });

    }

    /**
     *
     * @access public
     * @method get
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {NetworkException}
     * @throws {ProviderException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.get(options).then((response) => { console.log(response.data); });
     *
     */
    get(options) {

        const errorMessage = {
            method: 'HttpClient.get(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return new Promise((resolve) => {

            const { url, headers } = options;
            const { client, getResponseData, getHeaderWithKey, onError } = this;

            const request = client.createRequest(HttpMethod.GET, url, { headers });

            let response = null;

            /**
             *
             * @method onResponse
             * @param {Object} httpResponse
             *
             */
            request.onResponse = (httpResponse) => {
                response = httpResponse;
            };

            /**
             *
             * @method onComplete
             * @param {*} responseData
             * @param {Number} responseStatus
             * @returns {Promise<Object>}
             *
             */
            request.onComplete = (responseData, responseStatus) => {

                if (!responseStatus) {

                    return onError();

                }

                return resolve({
                    status: responseStatus,
                    data: getResponseData(responseData),
                    headers: {
                        get: (key) => {
                            return getHeaderWithKey(key, response);
                        }
                    }
                });

            };

            request.start();

        });

    }

    /**
     *
     * @access public
     * @method del
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {NetworkException}
     * @throws {ProviderException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.del(options).then((response) => { console.log(response.data); });
     *
     */
    del(options) {

        const errorMessage = {
            method: 'HttpClient.del(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return new Promise((resolve) => {

            const { url, headers, body } = options;
            const { client, getResponseData, getHeaderWithKey, onError } = this;

            const request = client.createRequest(HttpMethod.DELETE, url, { headers });

            let response = null;

            /**
             *
             * @method onResponse
             * @param {Object} httpResponse
             *
             */
            request.onResponse = (httpResponse) => {
                response = httpResponse;
            };

            /**
             *
             * @method onComplete
             * @param {*} responseData
             * @param {Number} responseStatus
             * @returns {Promise<Object>}
             *
             */
            request.onComplete = (responseData, responseStatus) => {

                if (!responseStatus) {

                    return onError();

                }

                return resolve({
                    status: responseStatus,
                    data: getResponseData(responseData),
                    headers: {
                        get: (key) => {
                            return getHeaderWithKey(key, response);
                        }
                    }
                });

            };

            request.sendBody(body);
            request.start();

        });

    }

    /**
     *
     * @access public
     * @method patch
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @note Trilithium does not natively support the HTTP PATCH method. To support legacy `HttpClient`
     * implementations, the SDK uses the `X-HTTP-Method-Override` header to
     * ensure the service properly handles the request.
     * @throws {NetworkException}
     * @throws {ProviderException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.patch(options).then((response) => { console.log(response.data); });
     *
     */
    patch(options) {

        const errorMessage = {
            method: 'HttpClient.patch(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return new Promise((resolve) => {

            const { url, headers, body } = options;
            const { client, getResponseData, getHeaderWithKey, onError } = this;

            const requestHeaders = Object.assign({}, headers);
            requestHeaders['X-HTTP-Method-Override'] = HttpMethod.PATCH;

            const request = client.createRequest(HttpMethod.POST, url, { headers: requestHeaders });

            let response = null;

            /**
             *
             * @method onResponse
             * @param {Object} httpResponse
             *
             */
            request.onResponse = (httpResponse) => {
                response = httpResponse;
            };

            /**
             *
             * @method onComplete
             * @param {*} responseData
             * @param {Number} responseStatus
             * @returns {Promise<Object>}
             *
             */
            request.onComplete = (responseData, responseStatus) => {

                if (!responseStatus) {

                    return onError();

                }

                return resolve({
                    status: responseStatus,
                    data: getResponseData(responseData),
                    headers: {
                        get: (key) => {
                            return getHeaderWithKey(key, response);
                        }
                    }
                });

            };

            request.sendBody(body);
            request.start();

        });

    }

    /**
     *
     * @access public
     * @method put
     * @param {Object} options
     * @throws {InvalidArgumentException} when options are incorrect or missing
     * @throws {NetworkException}
     * @throws {ProviderException}
     * @returns {Promise<Object>}
     *
     * @example
     * httpClient.put(options).then((response) => { console.log(response.data); });
     *
     */
    put(options) {

        const errorMessage = {
            method: 'HttpClient.put(options)',
            get options() {
                return `${this.method} ${this.optionsMismatchError}`;
            }
        };

        like(
            options,
            this.optionsBaseline,
            errorMessage.options,
            InvalidArgumentException
        );

        return new Promise((resolve) => {

            const { url, headers, body } = options;
            const { client, getResponseData, getHeaderWithKey, onError } = this;

            const request = client.createRequest(HttpMethod.PUT, url, { headers });

            let response = null;

            /**
             *
             * @method onResponse
             * @param {Object} httpResponse
             *
             */
            request.onResponse = (httpResponse) => {
                response = httpResponse;
            };

            /**
             *
             * @method onComplete
             * @param {*} responseData
             * @param {Number} responseStatus
             * @returns {Promise<Object>}
             *
             */
            request.onComplete = (responseData, responseStatus) => {

                if (!responseStatus) {

                    return onError();

                }

                return resolve({
                    status: responseStatus,
                    data: getResponseData(responseData),
                    headers: {
                        get: (key) => {
                            return getHeaderWithKey(key, response);
                        }
                    }
                });

            };

            request.sendBody(body);
            request.start();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @method getHeaderWithKey
     * @param {String} [key='']
     * @param {Object} [response={}]
     * @returns {String|Null}
     *
     */
    getHeaderWithKey(key = '', response = {}) {

        for (const keys in response.headers) {
            if (keys.toLowerCase() === key.toLowerCase()) {
                return response.headers[key];
            }
        }

        return null;

    }

}



// WEBPACK FOOTER //
// ./src/services/platforms/sony/trilithium/httpClient.js